import { defaultComponentKinds, specOf } from './catalog';
import { COMPONENT_KINDS } from './types';
import type { ComponentInstance, ComponentKind, FieldValue, Project } from './types';

export const SCHEMA_VERSION = 1;

/** 默认安全系数预留：20% */
export const DEFAULT_RESERVE_PCT = 20;

let counter = 0;

/** 本地唯一 ID。项目与部件都用它，避免引入额外依赖。 */
export function createId(prefix = 'id'): string {
  counter += 1;
  const random = Math.random().toString(36).slice(2, 8);
  return `${prefix}_${Date.now().toString(36)}${counter.toString(36)}${random}`;
}

export function createComponent(
  kind: ComponentKind,
  overrides: Partial<ComponentInstance> = {},
): ComponentInstance {
  const spec = specOf(kind);
  return {
    id: createId('c'),
    kind,
    qty: spec.defaultQty,
    fields: {},
    ...overrides,
  };
}

function nowIso(): string {
  return new Date().toISOString();
}

/** 新建空白项目：带 4 轴标准部件清单，但所有参数为空，交给引导向导逐项填写。 */
export function createBlankProject(name: string): Project {
  const timestamp = nowIso();
  return {
    id: createId('p'),
    name,
    schemaVersion: SCHEMA_VERSION,
    reservePct: DEFAULT_RESERVE_PCT,
    createdAt: timestamp,
    updatedAt: timestamp,
    components: defaultComponentKinds().map((kind) => createComponent(kind)),
  };
}

export function duplicateProject(project: Project, name: string): Project {
  const timestamp = nowIso();
  return {
    ...project,
    id: createId('p'),
    name,
    createdAt: timestamp,
    updatedAt: timestamp,
    components: project.components.map((component) => ({
      ...component,
      id: createId('c'),
      fields: { ...component.fields },
    })),
  };
}

export function touchProject(project: Project): Project {
  return { ...project, updatedAt: nowIso() };
}

/* ------------------------------------------------------------------ *
 * 反序列化 / 导入时的清洗
 * ------------------------------------------------------------------ */

function isKind(value: unknown): value is ComponentKind {
  return typeof value === 'string' && (COMPONENT_KINDS as readonly string[]).includes(value);
}

function cleanValue(value: unknown): FieldValue | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string') {
    // 保留用户原样输入的文本（数字字段里形如 "abc" 的内容会被引擎视为未填），
    // 但丢弃空串，保持「留空即未填」的语义。
    return value.trim().length > 0 ? value : undefined;
  }
  return undefined;
}

function cleanComponent(raw: unknown): ComponentInstance | null {
  if (!raw || typeof raw !== 'object') return null;
  const source = raw as Record<string, unknown>;
  if (!isKind(source.kind)) return null;

  const spec = specOf(source.kind);
  const allowedFields = new Set(spec.fields.map((field) => field.key));
  const fields: Record<string, FieldValue> = {};

  const rawFields = source.fields;
  if (rawFields && typeof rawFields === 'object') {
    for (const [key, value] of Object.entries(rawFields as Record<string, unknown>)) {
      if (!allowedFields.has(key)) continue;
      const cleaned = cleanValue(value);
      if (cleaned !== undefined) fields[key] = cleaned;
    }
  }

  const rawQty = source.qty;
  const qty =
    typeof rawQty === 'number' && Number.isFinite(rawQty) && rawQty >= 1 ? Math.floor(rawQty) : spec.defaultQty;

  const label = typeof source.label === 'string' && source.label.trim().length > 0 ? source.label.trim() : undefined;

  return {
    id: typeof source.id === 'string' && source.id.length > 0 ? source.id : createId('c'),
    kind: source.kind,
    qty,
    ...(label ? { label } : {}),
    fields,
  };
}

/** 把任意来源（localStorage / 导入的 JSON）的数据洗成合法项目；无法修复时返回 null。 */
export function normalizeProject(raw: unknown): Project | null {
  if (!raw || typeof raw !== 'object') return null;
  const source = raw as Record<string, unknown>;

  const components: ComponentInstance[] = [];
  if (Array.isArray(source.components)) {
    for (const item of source.components) {
      const cleaned = cleanComponent(item);
      if (cleaned) components.push(cleaned);
    }
  }

  const name = typeof source.name === 'string' && source.name.trim().length > 0 ? source.name.trim() : '未命名项目';
  const reserveRaw = typeof source.reservePct === 'number' ? source.reservePct : DEFAULT_RESERVE_PCT;
  const reservePct = Math.min(90, Math.max(0, Number.isFinite(reserveRaw) ? reserveRaw : DEFAULT_RESERVE_PCT));

  const createdAt = typeof source.createdAt === 'string' ? source.createdAt : nowIso();
  const updatedAt = typeof source.updatedAt === 'string' ? source.updatedAt : createdAt;
  const note = typeof source.note === 'string' && source.note.trim().length > 0 ? source.note : undefined;

  return {
    id: typeof source.id === 'string' && source.id.length > 0 ? source.id : createId('p'),
    name,
    ...(note ? { note } : {}),
    schemaVersion: SCHEMA_VERSION,
    reservePct,
    createdAt,
    updatedAt,
    components,
  };
}

export function normalizeProjects(raw: unknown): Project[] {
  if (!Array.isArray(raw)) return [];
  const projects: Project[] = [];
  for (const item of raw) {
    const project = normalizeProject(item);
    if (project) projects.push(project);
  }
  return projects;
}

/** 部件目录里全部字段都为空的项目 —— 用于「是否已填过参数」的判断 */
export function isEmptyProject(project: Project): boolean {
  return project.components.every((component) =>
    Object.values(component.fields).every(
      (value) => value === undefined || value === null || `${value}`.trim() === '',
    ),
  );
}
